import { useEffect, useRef } from 'react';
import Echo from '../websocket';

const NOTIFICATION_EVENT = '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated';

interface EventListen {
  event: string;
  callback: (event: any) => void;
}

interface UseWebSocketParams {
  channelName: string;
  privateChannel?: boolean;
  /** Skips the subscription entirely, e.g. while the channel name still depends on data being loaded. */
  enabled?: boolean;
  notification?: (event: any) => void;
  listen?: EventListen | EventListen[];
}

/**
 * Remounting a component - collapsing the sidebar, changing route - drops the last subscriber
 * of a channel for a single commit. Waiting before leaving avoids an unsubscribe/resubscribe
 * round-trip during which every event published would be lost.
 */
const LEAVE_GRACE_MS = 5_000;

/**
 * Echo caches a single channel instance per name, so several components end up sharing the
 * same subscription. Reference counting makes sure the channel is only left once its last
 * consumer unmounts - leaving it earlier silently kills the listeners of every other one.
 */
const channelSubscribers = new Map<string, number>();
const pendingLeaves = new Map<string, number>();

const getChannelKey = (channelName: string, privateChannel: boolean) =>
  (privateChannel ? `private-${channelName}` : channelName);

const joinChannel = (channelName: string, privateChannel: boolean) => {
  const key = getChannelKey(channelName, privateChannel);

  window.clearTimeout(pendingLeaves.get(key));
  pendingLeaves.delete(key);

  channelSubscribers.set(key, (channelSubscribers.get(key) ?? 0) + 1);

  return privateChannel ? Echo.private(channelName) : Echo.channel(channelName);
};

const leaveChannel = (channelName: string, privateChannel: boolean) => {
  const key = getChannelKey(channelName, privateChannel);
  const subscribers = Math.max(0, (channelSubscribers.get(key) ?? 1) - 1);

  if (subscribers > 0) {
    channelSubscribers.set(key, subscribers);
    return;
  }

  channelSubscribers.delete(key);

  pendingLeaves.set(
    key,
    window.setTimeout(() => {
      pendingLeaves.delete(key);

      if (channelSubscribers.has(key)) {
        return;
      }

      // Echo indexes its channels by the prefixed name, so the unprefixed one never matches.
      Echo.leaveChannel(key);
    }, LEAVE_GRACE_MS),
  );
};

const toListenList = (listen: UseWebSocketParams['listen']): EventListen[] => {
  if (!listen) {
    return [];
  }

  return Array.isArray(listen) ? listen : [listen];
};

export default function useWebSocket({
  channelName,
  privateChannel = true,
  enabled = true,
  notification,
  listen,
}: UseWebSocketParams) {
  const listenRef = useRef(toListenList(listen));
  const notificationRef = useRef(notification);

  listenRef.current = toListenList(listen);
  notificationRef.current = notification;

  const isActive = enabled && !!channelName;

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const channel = joinChannel(channelName, privateChannel);

    let notificationCallback: ((event: any) => void) | undefined;

    if (notificationRef.current) {
      notificationCallback = (event: any) => notificationRef.current?.(event);

      channel.notification(notificationCallback);
    }

    const boundListeners = listenRef.current.map((item, index) => {
      const callback = (event: any) => {
        listenRef.current[index]?.callback?.(event);
      };

      channel.listen(item.event, callback);

      return { event: item.event, callback };
    });

    return () => {
      if (notificationCallback) {
        channel.stopListening(NOTIFICATION_EVENT, notificationCallback);
      }

      boundListeners.forEach((item) => channel.stopListening(item.event, item.callback));

      leaveChannel(channelName, privateChannel);
    };
  }, [isActive, privateChannel, channelName]);
}
